import { Box, Link as MuiLink, Tooltip, Typography } from '@mui/material'
import { Favorite } from '@mui/icons-material'
import { type GridColDef } from '@mui/x-data-grid-pro'
import {
    type GetRosterSearchV2Query,
    type RosterSourceStatus,
    SortIndexField,
} from '__generated__/graphql'
import { formatDate } from 'date-fns'
import { Link } from 'react-router-dom'
import { recruiterRouteConfig } from 'recruiter/routes'
import RosterStatusPill from 'recruiter/components/RosterStatusPill'
import { ProcessStatusPill } from 'recruiter/components/ProcessStatusPill'
import ProcessStatusEditWithAddToRoster from 'recruiter/pages/Rosters/RosterProfile/EditCells/ProcessStatusEditWithAddToRoster'
import PeopleProcessStatusCell from './PeopleProcessStatusCell'
import { GetRosterSearchV2 } from 'recruiter/contexts'
import { SourceStatusPill } from 'shared/components/SourceStatusPill'
import { type RosterSortField } from 'shared/hooks/rosterSqlSearchUrlState'

export type RosterTableRowModel =
    GetRosterSearchV2Query['rosterSearchV2']['data'][number]

export type RosterTableColumnDefinition = GridColDef<RosterTableRowModel> & {
    sortIndexField?: SortIndexField
    sortField?: RosterSortField
}

export const onSearchesColumns: RosterTableColumnDefinition[] = [
    {
        field: 'rosterName',
        headerName: 'Search',
        flex: 1,
        sortable: true,
        sortIndexField: SortIndexField.RosterName,
        sortField: 'name',
        renderCell: ({ row }) => (
            <MuiLink
                component={Link}
                to={`${recruiterRouteConfig.rosters.path}/${row.id}`}
            >
                {row.name}
            </MuiLink>
        ),
    },
    {
        field: 'company',
        headerName: 'Company',
        flex: 1,
        sortable: true,
        sortIndexField: SortIndexField.RosterCompany,
        sortField: 'company',
        renderCell: ({ row }) =>
            row.company ? (
                <MuiLink
                    component={Link}
                    to={`${recruiterRouteConfig.companies.path}/${row.company.id}`}
                >
                    {row.company.name}
                </MuiLink>
            ) : (
                '—'
            ),
    },
    {
        field: 'fundSize',
        headerName: 'Fund Size',
        flex: 1,
        sortable: true,
        sortField: 'fundSize',
        renderCell: ({ row }) => {
            const str =
                row.fundSize
                    ?.split(';')
                    .map((s) => s.trim())
                    .join(', ') || '—'
            return <span title={str}>{str}</span>
        },
    },
    {
        field: 'consultants',
        headerName: 'Consultants',
        width: 100,
        sortable: true,
        sortField: 'consultants',
        valueGetter: (_, row) => (row.consultants ? 'Yes' : 'No'),
    },
    {
        field: 'visaStatuses',
        headerName: 'Visa',
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => {
            const str =
                row.visaStatuses
                    ?.split(';')
                    .map((s) => s.trim())
                    .join(', ') || '—'
            return <span title={str}>{str}</span>
        },
    },
    {
        field: 'added',
        headerName: 'Added',
        width: 110,
        sortable: true,
        sortIndexField: SortIndexField.RosterCreatedAt,
        sortField: 'createdAt',
        valueGetter: (_, row) => row.createdAt,
        renderCell: ({ row }) =>
            row.createdAt
                ? formatDate(new Date(row.createdAt), 'MM/dd/yyyy')
                : '—',
    },
    {
        field: 'clientFavorites',
        headerName: 'Favorited',
        width: 90,
        sortable: true,
        sortField: 'clientFavoritesCount',
        renderCell: ({ row }) => {
            const count = (row as { clientFavoritesCount?: number | null })
                .clientFavoritesCount
            if (!count || count === 0) return null
            return (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                    position="relative"
                >
                    <Favorite fontSize="medium" color="error" />
                    <Typography
                        variant="caption"
                        position="absolute"
                        fontWeight="bold"
                        fontSize={10}
                        sx={{ color: 'common.white', mt: 0.25 }}
                    >
                        {count}
                    </Typography>
                </Box>
            )
        },
    },
    {
        field: 'internalNote',
        headerName: 'Internal Note',
        flex: 1.5,
        sortable: true,
        sortIndexField: SortIndexField.RosterInternalNote,
        sortField: 'internalNote',
        renderCell: ({ row }) => {
            const note = (row as { internalNote?: string | null }).internalNote
            if (!note) return null
            return (
                <Box
                    display="flex"
                    alignItems="center"
                    height="100%"
                    width="100%"
                >
                    <Tooltip title={note}>
                        <Typography
                            variant="body2"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {note}
                        </Typography>
                    </Tooltip>
                </Box>
            )
        },
    },
    {
        field: 'processStatus',
        headerName: 'Process Status',
        flex: 1,
        sortable: true,
        sortIndexField: SortIndexField.ProcessStatus,
        sortField: 'processStatus',
        renderCell: ({ row }) => {
            const rosterCandidateId = (
                row as { rosterCandidateId?: string | null }
            ).rosterCandidateId
            if (rosterCandidateId) return <PeopleProcessStatusCell row={row} />
            const processStatusId = (row as { processStatusId?: string | null })
                .processStatusId
            if (!processStatusId) return '—'
            return <ProcessStatusPill processStatusId={processStatusId} />
        },
    },
    {
        field: 'processStatusUpdated',
        headerName: 'Process Status Updated At',
        width: 110,
        sortable: true,
        sortIndexField: SortIndexField.ProcessStatusUpdatedAt,
        sortField: 'processStatusUpdatedAt',
        renderCell: ({ row }) => {
            const updatedAt = (
                row as { processStatusUpdatedAt?: string | null }
            ).processStatusUpdatedAt
            if (!updatedAt) return '—'
            return formatDate(new Date(updatedAt), 'MM/dd/yyyy')
        },
    },
    {
        field: 'sourceStatus',
        headerName: 'Source Status',
        flex: 1,
        sortable: true,
        sortIndexField: SortIndexField.SourceStatus,
        sortField: 'sourceStatus',
        renderCell: ({ row }) => {
            const sourceStatus = (
                row as { sourceStatus?: RosterSourceStatus | null }
            ).sourceStatus
            if (!sourceStatus) return '—'
            return <SourceStatusPill status={sourceStatus} />
        },
    },
    {
        field: 'rosterStatus',
        headerName: 'Search Status',
        flex: 1,
        sortable: true,
        sortIndexField: SortIndexField.RosterStatus,
        sortField: 'status',
        renderCell: ({ row }) => <RosterStatusPill status={row.status ?? ''} />,
    },
]

export const otherSearchesColumns = (
    candidateId: string
): RosterTableColumnDefinition[] => [
    {
        field: 'rosterName',
        headerName: 'Search',
        flex: 1,
        sortable: true,
        sortIndexField: SortIndexField.RosterName,
        sortField: 'name',
        renderCell: ({ row }) => (
            <MuiLink
                component={Link}
                to={`${recruiterRouteConfig.rosters.path}/${row.id}`}
            >
                {row.name}
            </MuiLink>
        ),
    },
    {
        field: 'company',
        headerName: 'Company',
        flex: 1,
        sortable: true,
        sortIndexField: SortIndexField.RosterCompany,
        sortField: 'company',
        renderCell: ({ row }) =>
            row.company ? (
                <MuiLink
                    component={Link}
                    to={`${recruiterRouteConfig.companies.path}/${row.company.id}`}
                >
                    {row.company.name}
                </MuiLink>
            ) : (
                '—'
            ),
    },
    {
        field: 'fundSize',
        headerName: 'Fund Size',
        flex: 1,
        sortable: true,
        sortField: 'fundSize',
        renderCell: ({ row }) => {
            const str =
                row.fundSize
                    ?.split(';')
                    .map((s) => s.trim())
                    .join(', ') || '—'
            return <span title={str}>{str}</span>
        },
    },
    {
        field: 'consultants',
        headerName: 'Consultants',
        width: 100,
        sortable: true,
        sortField: 'consultants',
        valueGetter: (_, row) => (row.consultants ? 'Yes' : 'No'),
    },
    {
        field: 'visaStatuses',
        headerName: 'Visa',
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => {
            const str =
                row.visaStatuses
                    ?.split(';')
                    .map((s) => s.trim())
                    .join(', ') || '—'
            return <span title={str}>{str}</span>
        },
    },
    {
        field: 'processStatus',
        headerName: 'Process Status',
        width: 240,
        sortable: false,
        renderCell: (params) =>
            params.row.rosterCandidateId ? (
                <PeopleProcessStatusCell row={params.row} />
            ) : (
                <ProcessStatusEditWithAddToRoster
                    {...params}
                    row={{ ...params.row, status: null }}
                    candidateId={candidateId}
                    rosterId={params.row.id}
                    canEdit={params.row.canEdit ?? false}
                    refetchQueries={[GetRosterSearchV2]}
                />
            ),
    },
    {
        field: 'rosterStatus',
        headerName: 'Search Status',
        flex: 1,
        sortable: true,
        sortIndexField: SortIndexField.RosterStatus,
        sortField: 'status',
        renderCell: ({ row }) => <RosterStatusPill status={row.status ?? ''} />,
    },
]
